import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Bell, History, Home, Trophy, User, Mountain, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RealtimeProvider, useRealtime } from "@/lib/realtime";
import { fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar, CatMascot } from "@/components/Brand";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POPUP_TYPES = [
  "queue_approaching",
  "ticket_called",
  "ticket_cancelled",
  "payment_approved",
];
const SHOWN_KEY = "iym_shown_notifs";

function getShown() {
  try {
    return new Set(JSON.parse(localStorage.getItem(SHOWN_KEY) || "[]"));
  } catch {
    return new Set();
  }
}
function markShown(id) {
  const s = getShown();
  s.add(id);
  localStorage.setItem(SHOWN_KEY, JSON.stringify([...s].slice(-200)));
}

function NotificationPopup({ n, onClose }) {
  const Icon = n?.type === "ticket_cancelled" ? AlertTriangle : Mountain;
  const happy = ["ticket_called", "queue_approaching", "payment_approved"].includes(n?.type);
  return (
    <Dialog open={!!n} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm border-2 border-[#1A3323]" data-testid="notification-popup">
        <DialogHeader>
          {happy ? <CatMascot jump className="mx-auto w-28" /> : (
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-[#E3EDD9]">
            <Icon className="h-7 w-7 text-[#1A3323]" />
          </div>)}
          <DialogTitle className="text-center text-xl" data-testid="notification-popup-title">{n?.title}</DialogTitle>
          <DialogDescription className="text-center text-base text-[#242926]" data-testid="notification-popup-message">{n?.message}</DialogDescription>
        </DialogHeader>
        {n?.type === "queue_approaching" && (
          <div className="grid grid-cols-3 gap-2 rounded-xl bg-[#F5F3EB] p-3 text-center text-sm">
            <div><div className="text-xs text-muted-foreground">Tiket</div><div className="font-mono font-extrabold">{n.meta?.number}</div></div>
            <div><div className="text-xs text-muted-foreground">Jalur</div><div className="font-bold">{n.meta?.route}</div></div>
            <div><div className="text-xs text-muted-foreground">Posisi</div><div className="font-mono font-extrabold">~#{n.meta?.position}</div></div>
          </div>
        )}
        {n?.profile_name && <p className="text-center text-xs text-muted-foreground">Profil: {n.profile_name}</p>}
        <Button className="h-12 rounded-full font-bold" onClick={onClose} data-testid="notification-popup-ok">Oke, siap!</Button>
      </DialogContent>
    </Dialog>
  );
}

function NotificationCenter() {
  const { subscribe, connected } = useRealtime();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState(null);
  const queue = useRef([]);
  const popupRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/me/notifications");
      setItems(data.items);
      setUnread(data.unread);
      const shown = getShown();
      const fresh = Date.now() - 10 * 60 * 1000;
      data.items.filter((n) => !n.read && !shown.has(n.id)).reverse().forEach((n) => (Date.parse(n.created_at) >= fresh ? handle(n) : markShown(n.id)));
    } catch {
      /* handled by interceptor */
    }
  };
  const handle = (n) => {
    if (getShown().has(n.id)) return;
    markShown(n.id);
    if (POPUP_TYPES.includes(n.type)) {
      queue.current.push(n);
      // no side effects inside setState updater (StrictMode double-invokes it and dropped the popup)
      if (!popupRef.current) {
        popupRef.current = queue.current.shift();
        setPopup(popupRef.current);
      }
    } else {
      toast(n.title, { description: n.message });
    }
  };
  const closePopup = () => {
    popupRef.current = queue.current.shift() || null;
    setPopup(popupRef.current);
  };

  useEffect(() => {
    load();
    const un = subscribe((m) => {
      if (m.type === "notification") {
        handle(m.notification);
        load();
      }
    });
    return un;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);
  useEffect(() => {
    const id = setInterval(load, connected ? 30000 : 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const openSheet = async () => {
    setOpen(true);
    if (unread) {
      await api.post("/me/notifications/read");
      setUnread(0);
    }
  };

  return (
    <>
      <button onClick={openSheet} className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white ring-1 ring-[#1A3323]/10 transition-colors hover:bg-[#EDE9DC]" data-testid="notification-bell-button">
        <Bell className="h-5 w-5 text-[#1A3323]" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1A3323] px-1 text-[10px] font-bold text-white" data-testid="notification-unread-count">{unread}</span>
        )}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[90vw] max-w-sm overflow-y-auto" data-testid="notification-sheet">
          <SheetHeader><SheetTitle>Notifikasi</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2">
            {items.length === 0 && <p className="text-sm text-muted-foreground">Belum ada notifikasi.</p>}
            {items.map((n) => (
              <div key={n.id} className="rounded-xl bg-[#F5F3EB] p-3" data-testid="notification-item">
                <div className="text-sm font-bold text-[#1A3323]">{n.title}</div>
                <div className="text-sm">{n.message}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">{n.profile_name} · {fmtDateTime(n.created_at)}</div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
      <NotificationPopup n={popup} onClose={closePopup} />
    </>
  );
}

function ProfileSwitcher() {
  const { profiles, activeProfile, selectProfile } = useAuth();
  if (profiles.length < 2) return <div className="truncate font-bold text-[#1A3323]" data-testid="active-profile-name">{activeProfile?.full_name}</div>;
  return (
    <Select value={activeProfile?.id} onValueChange={selectProfile}>
      <SelectTrigger className="h-10 max-w-[200px] rounded-full border-[#1A3323]/15 bg-white font-semibold" data-testid="profile-switcher">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {profiles.map((p) => (
          <SelectItem key={p.id} value={p.id} data-testid={`profile-option-${p.id}`}>
            {p.full_name} {p.is_child ? "(anak)" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function HeaderProfile() {
  const { activeProfile } = useAuth();
  return (
    <div className="mt-1 flex items-center gap-2">
      <Avatar name={activeProfile?.full_name} size={30} />
      <div className="min-w-0 flex-1"><ProfileSwitcher /></div>
    </div>
  );
}

const NAV = [
  { to: "/app", icon: Home, label: "Home", end: true, id: "home" },
  { to: "/app/riwayat", icon: History, label: "Riwayat", id: "history" },
  { to: "/app/leaderboard", icon: Trophy, label: "Leaderboard", id: "leaderboard" },
  { to: "/app/profil", icon: User, label: "Profil", id: "profile" },
];

function ConnDot() {
  const { connected } = useRealtime();
  return <span title={connected ? "Realtime aktif" : "Mode cadangan (polling)"} data-testid="realtime-status" className={cn("h-2 w-2 rounded-full", connected ? "bg-green-500" : "bg-amber-500")} />;
}

export default function ParticipantLayout() {
  return (
    <RealtimeProvider>
      <div className="mx-auto min-h-screen max-w-md bg-[#F7F4EA] pb-28">
        <header className="sticky top-0 z-30 border-b border-[#1A3323]/10 bg-[#F7F4EA]/90 px-4 pb-2.5 pt-2 backdrop-blur-xl">
          <div className="flex items-center gap-2">
            <img src="/logo-full.png" alt="iyalagi.manjat" className="h-14 w-auto" data-testid="brand-logo" />
            <div className="flex-1" />
            <LanguageSelector />
            <ConnDot />
            <NotificationCenter />
          </div>
          <HeaderProfile />
        </header>
        <main className="px-4 pt-5"><Outlet /></main>
        <nav className="fixed inset-x-0 bottom-0 z-30 mx-auto max-w-md border-t border-[#1A3323]/10 bg-[#FDFCF8]/95 backdrop-blur-xl">
          <div className="grid grid-cols-4">
            {NAV.map(({ to, icon: Icon, label, end, id }) => (
              <NavLink key={to} to={to} end={end} data-testid={`nav-${id}`}
                className={({ isActive }) => cn("flex flex-col items-center gap-1 py-3 text-[11px] font-semibold transition-colors", isActive ? "text-[#1A3323]" : "text-[#242926]/45")}>
                {({ isActive }) => (
                  <>
                    <span className={cn("flex h-8 w-12 items-center justify-center rounded-full transition-colors", isActive && "bg-[#1A3323] text-[#FDFCF8]")}><Icon className="h-5 w-5" /></span>
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </RealtimeProvider>
  );
}
